import { Dispatch, SetStateAction } from 'react'
import { Box, Button, useTheme } from '@mui/material'
import { DataGrid, gridClasses } from '@mui/x-data-grid'
import { ComplianceStatus } from '../../../../types'
import { standardDateFormat } from '../../../../helpers/date'
import { Autoclave, OpenSporetestModal, SporetestingResult } from '../types'
import CompliancePlip from '../../../../components/CompliancePlip'
import EditIcon from '@mui/icons-material/Edit'
import RepeatIcon from '@mui/icons-material/Repeat'

type AutoclaveTable = {
  autoclave: Autoclave,
  sporetestsByAutoclaveId: Map<number, SporetestingResult[]>,
  setOpenMarkAsAddressed: Dispatch<SetStateAction<boolean>>,
  setOpenReassignSporetest: Dispatch<SetStateAction<boolean>>,
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>,
  setSelectedSporeTest: Dispatch<SetStateAction<SporetestingResult | null>>,
}

const AutoclaveTable = (props: AutoclaveTable) => {
  const {
    autoclave,
    sporetestsByAutoclaveId,
    setOpenMarkAsAddressed,
    setOpenReassignSporetest,
    setOpenSporetestModal,
    setSelectedSporeTest,
  } = props
  
  const theme = useTheme()

  const autoclaveTestResults = sporetestsByAutoclaveId.get(Number(autoclave.autoclave_id)) ?? []
  
  const getComplianceColor = (result: string) => {
    if (result === 'pass') {
      return ComplianceStatus.Ok
    } else if (result === 'invalid' || result === 'fail') {
      return ComplianceStatus.Failed
    }
    return ComplianceStatus.Inactive
  }

  const handleMarkAsAddressed = (sporetest: SporetestingResult) => {
    setSelectedSporeTest(sporetest)
    setOpenMarkAsAddressed(true)
  }

  const handleReassignSporetest = (sporetest: SporetestingResult) => {
    setSelectedSporeTest(sporetest)
    setOpenReassignSporetest(true)
  }

  const handleOpenSporeTestModal = (sporetest: SporetestingResult) => {
    setOpenSporetestModal({
      open: true,
      autoclaveId: Number(autoclave.autoclave_id),
      testType: sporetest.test_type,
      sporetest,
    })
  }

  return (
    <DataGrid
      sx={{
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        borderRadius: '0',
        color: theme.palette.color50555b.main,
        [`& .${gridClasses.cell}`]: {
          py: 1
        }
      }}
      rows={autoclaveTestResults}
      getRowId={row => row.test_result_id}
      columns={[
        {
          field: 'test_result',
          headerName: '',
          width: 50,
          sortable: false,
          renderCell: (params) => {
            const status = getComplianceColor(params.row.test_result ?? '')

            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                <CompliancePlip message={params.row.test_result ?? ''} status={status} />
              </Box>
            )
          }
        },
        {
          field: 'result_description',
          headerName: 'Result',
          flex: 1,
          minWidth: 450,
          renderCell: (params) => <Box>{params.row.result_description || params.row.test_result}</Box>
        },
        {
          field: 'test_number',
          headerName: 'Test Number',
          flex: 1,
          minWidth: 135,
          renderCell: (params) => <Box>{params.row.test_number ?? ''}</Box>
        },
        {
          field: 'sample_date',
          headerName: 'Test Date',
          flex: 1,
          minWidth: 125,
          renderCell: (params) => standardDateFormat(params.row.sample_date ?? '')
        },
        {
          field: 'received_date',
          headerName: 'Received Date',
          flex: 1,
          minWidth: 125,
          renderCell: (params) => standardDateFormat(params.row.received_date ?? '')
        },
        {
          field: 'test_date',
          headerName: 'Result Date',
          flex: 1,
          minWidth: 125,
          renderCell: (params) => standardDateFormat(params.row.test_date ?? ''),
          getSortComparator: (sortDirection) => {
            const modifier = sortDirection === 'desc' ? -1 : 1

            return (a, b) => {
              const date = Date.parse(a)
              const date2 = Date.parse(b)

              if (isNaN(date) && isNaN(date2)) {
                return 0
              } else if (isNaN(date) && !isNaN(date2)) {
                return 1 * modifier
              } else if (!isNaN(date) && isNaN(date2)) {
                return -1 * modifier
              } else {
                return (date - date2) * modifier
              }
            }
          },
        },
        {
          field: 'test_operator',
          headerName: 'Operator',
          flex: 1,
          minWidth: 125,
          renderCell: (params) => <Box sx={{ display: 'flex', alignItems: 'center' }}>{params.row.test_operator ?? 'Unknown'}</Box>
        },
        {
          field: 'note',
          headerName: 'Corrective Action',
          flex: 1,
          minWidth: 200,
          sortable: false,
          renderCell: (params) => (
            <Box sx={{ display: 'flex' }}>
              {params.row.note && (
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} columnGap='8px'>
                  <span>
                    {params.row.note}
                  </span>
                  <EditIcon fontSize="small" onClick={() => handleMarkAsAddressed(params.row)} />
                </Box>
              )}
              {params.row.note === null && (params.row.test_result === 'fail' || params.row.test_result === 'invalid') && (
                <Button sx={{ mr: 2 }} variant="contained" size="small" onClick={() => handleMarkAsAddressed(params.row)}>
                  Mark as Addressed
                </Button>
              )}
            </Box>
          )
        },
        {
          field: 'id',
          headerName: 'Reassign Spore Test',
          width: 150,
          sortable: false,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <RepeatIcon sx={{ cursor: 'pointer' }} onClick={() => handleReassignSporetest(params.row)} />
            </Box>
          )
        },
        {
          field: 'test_result_id',
          headerName: 'Edit',
          width: 50,
          sortable: false,
          renderCell: (params) => (
            <Box onClick={() => handleOpenSporeTestModal(params.row)} sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
              {(params.row.test_type === 'inoffice' || params.row.test_type === 'mesa') && <EditIcon fontSize="small" />}
            </Box>
          )
        }
      ]}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10
          }
        },
        sorting: {
          sortModel: [
            {
              field: `test_date`,
              sort: 'desc'
            }
          ]
        }
      }}
      pageSizeOptions={[5, 10, 20, 25, 50, 100]}
      density="compact"
      getRowHeight={() => 'auto'}
    />    
  )
}

export default AutoclaveTable
